import { Moon, Pause, Play, Sun, SunMoon } from "lucide-react";
import { controller } from "../app/controller.ts";
import { mcClock } from "../engine/materials.ts";
import { useApp } from "../state/store.ts";
import { usePopover } from "./usePopover.ts";

const PRESETS: [string, number][] = [["Sunrise", 23500], ["Noon", 6000], ["Sunset", 12300], ["Night", 18000]];
const SPEEDS: [string, number][] = [["1 day = 2 min", 200], ["1 day = 20 min (real Minecraft)", 20], ["1 day = 30 s", 800]];

function isDay(t: number) {
  return t < 12500 || t > 23300;
}

function icon(on: boolean, t: number) {
  if (!on) return <SunMoon size={14} aria-hidden="true" />;
  return isDay(t) ? <Sun size={14} aria-hidden="true" className="sun" /> : <Moon size={14} aria-hidden="true" className="moon" />;
}

/** Menubar control for Minecraft lighting and the time of day. */
export function TimeControl() {
  const l = useApp((s) => s.lighting);
  const worldTime = useApp((s) => s.level?.dayTime);
  const m = usePopover();
  const near = (t: number) => Math.abs(((l.time - t + 36000) % 24000) - 12000) > 11800;

  return (
    <div className="time-group" ref={m.ref}>
      <button className="vb-btn time-btn" aria-pressed={l.on} aria-expanded={m.open} onClick={m.toggle} title="Day & night lighting · L">
        <span className="time-icon">{icon(l.on, l.time)}</span>
        <span className="tabular">{l.on ? mcClock(l.time) : "Light"}</span>
      </button>
      {m.open && (
        <div className="menu time-menu" role="dialog" aria-label="Day and night">
          <label className="switch-row">
            <span className="grow">
              <span className="field-label">Minecraft lighting</span>
              <span className="field-hint">Torches glow, caves are dark</span>
            </span>
            <kbd>L</kbd>
            <input type="checkbox" className="switch" role="switch" checked={l.on} onChange={(e) => controller.setLighting({ on: e.target.checked })} />
          </label>
          <div className="time-slider">
            <input type="range" min={0} max={23999} step={50} value={Math.round(l.time)} onChange={(e) => controller.setLighting({ time: +e.target.value, playing: false })} aria-label="Time of day" />
            <div className="time-ticks mono"><span>06:00</span><span>12:00</span><span>18:00</span><span>00:00</span><span>06:00</span></div>
          </div>
          <div className="seg time-presets">
            {PRESETS.map(([name, t]) => <button key={name} aria-pressed={l.on && near(t)} onClick={() => controller.setLighting({ time: t, playing: false })}>{name}</button>)}
          </div>
          {worldTime !== undefined && worldTime !== null && (
            <button className="btn btn-sm btn-outline in-game" onClick={() => controller.setLighting({ time: worldTime, playing: false })} title="The time of day saved in the world">In-game {mcClock(worldTime)}</button>
          )}
          <div className="time-play">
            <button className={`btn btn-sm ${l.playing ? "btn-soft" : "btn-outline"}`} onClick={() => controller.setLighting({ playing: !l.playing })}>
              {l.playing ? <><Pause size={14} aria-hidden="true" />Pause</> : <><Play size={14} aria-hidden="true" />Play day cycle</>}
            </button>
            <select value={l.speed} onChange={(e) => controller.setLighting({ speed: +e.target.value })} aria-label="Speed of the day cycle">
              {SPEEDS.map(([n, v]) => <option key={v} value={v}>{n}</option>)}
            </select>
          </div>
          <p className="field-hint">Uses the light levels Minecraft saved in the world. Off = the always-bright map view.</p>
        </div>
      )}
    </div>
  );
}
